import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, Check } from 'lucide-react';
import { VocabularyWord } from '../components/VocabularyCard';
import { FlashCard } from '../components/FlashCard';
interface ReviewPageProps {
  words: VocabularyWord[];
  onExit: () => void;
}
export function ReviewPage({
  words,
  onExit
}: ReviewPageProps) {
  const [queue, setQueue] = useState<VocabularyWord[]>([...words]);
  const [isFlipped, setIsFlipped] = useState(false);
  const [direction, setDirection] = useState(0);
  // Track initial count for progress bar
  const [initialCount] = useState(words.length);
  const currentWord = queue[0];
  const isComplete = queue.length === 0;
  const progress = (initialCount - queue.length) / initialCount * 100;
  const handleGood = () => {
    setDirection(1);
    setIsFlipped(false);
    setTimeout(() => {
      setQueue(prev => prev.slice(1));
    }, 200);
  };
  const handleAgain = () => {
    setDirection(-1);
    setIsFlipped(false);
    setTimeout(() => {
      setQueue(prev => {
        const [first, ...rest] = prev;
        return [...rest, first];
      });
    }, 200);
  };
  return <div className="min-h-screen bg-[#FDFBF7] text-gray-800 font-sans flex flex-col" data-id="element-114">
      {/* Header */}
      <header className="px-6 py-6 flex items-center justify-between sticky top-0 bg-[#FDFBF7]/80 backdrop-blur-sm z-10" data-id="element-115">
        <button onClick={onExit} className="p-2 -ml-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-116">
          <X size={24} data-id="element-117" />
        </button>
        <div className="flex flex-col items-center" data-id="element-118">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1" data-id="element-119">
            Review
          </span>
          <div className="flex items-center gap-2" data-id="element-120">
            <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-121">
              <motion.div className="h-full bg-[#10B981]" initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.3
            }} data-id="element-122" />
            </div>
            <span className="text-xs font-medium text-gray-500 w-8" data-id="element-123">
              {queue.length} left
            </span>
          </div>
        </div>
        <div className="w-10" data-id="element-124" /> {/* Spacer */}
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-4 w-full max-w-md mx-auto relative overflow-y-auto pb-32" data-id="element-125">
        <AnimatePresence mode="wait" custom={direction} data-id="element-126">
          {!isComplete ? <motion.div key={currentWord.id} initial={{
          x: direction * 50,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: direction * -50,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="w-full" data-id="element-127">
              <FlashCard word={currentWord} isFlipped={isFlipped} onFlip={() => setIsFlipped(!isFlipped)} data-id="element-128" />
            </motion.div> : <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="text-center p-8 bg-white rounded-3xl shadow-sm border border-gray-100 w-full" data-id="element-129">
              <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-130">
                <Check size={40} strokeWidth={3} data-id="element-131" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-132">
                All Done!
              </h2>
              <p className="text-gray-500 mb-8" data-id="element-133">
                You've reviewed all your cards for today.
              </p>
              <button onClick={onExit} className="w-full bg-[#10B981] text-white font-bold text-lg py-4 rounded-2xl shadow-lg shadow-green-200 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200" data-id="element-134">
                Finish Review
              </button>
            </motion.div>}
        </AnimatePresence>
      </main>

      {/* Footer Controls - Fixed */}
      {!isComplete && <footer className="fixed bottom-0 left-0 right-0 px-6 py-8 bg-[#FDFBF7]/95 backdrop-blur-sm border-t border-gray-100" data-id="element-135">
          <div className="flex items-center justify-between gap-4 w-full max-w-md mx-auto" data-id="element-136">
            <button onClick={handleAgain} className="flex-1 bg-white text-gray-600 font-bold text-lg py-4 rounded-2xl shadow-sm border border-gray-200 hover:bg-gray-50 hover:shadow-md active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2" data-id="element-137">
              <RotateCcw size={20} data-id="element-138" />
              Again
            </button>

            <button onClick={handleGood} className="flex-[2] bg-[#10B981] text-white font-bold text-lg py-4 rounded-2xl shadow-lg shadow-green-200 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2" data-id="element-139">
              <Check size={20} strokeWidth={3} data-id="element-140" />
              Good
            </button>
          </div>
        </footer>}
    </div>;
}